/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { state } from '@/cart/lib/state';
import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';
import { events } from '@adobe-commerce/event-bus';
import { REMOVE_GIFT_CARD_ON_CART_MUTATION } from './graphql/RemoveGiftCardFromCartMutation';
import { publishCartUpdateEvents } from '@/cart/lib/acdl';

export const removeGiftCardFromCart = async (
  giftCardCode: string
): Promise<CartModel | null> => {
  const cartId = state.cartId;

  if (!cartId) {
    throw Error('Cart ID is not set');
  }

  return fetchGraphQl(REMOVE_GIFT_CARD_ON_CART_MUTATION, {
    variables: {
      cartId,
      giftCardCode,
    },
  }).then(({ errors, data }) => {
    // handle errors
    const _errors = [
      ...(data?.addProductsToCart?.user_errors ?? []),
      ...(errors ?? []),
    ];

    if (_errors.length > 0) return handleFetchError(_errors);

    const payload = transformCart(data.removeGiftCardFromCart.cart);

    // emit events
    events.emit('cart/updated', payload);
    events.emit('cart/data', payload);

    if (payload) {
      publishCartUpdateEvents(payload, [], state.locale ?? 'en-US');
    }

    return payload;
  });
};
